import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { withStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import Tooltip from '@material-ui/core/Tooltip';
import CircularProgress from '@material-ui/core/CircularProgress';
import IconButton from '@material-ui/core/IconButton';
import SvgClose from '@material-ui/icons/Close';
import SvgViewer from '@material-ui/icons/Explore';
import SvgTasking from '@material-ui/icons/CreateNewFolder';
import SvgReport from '@material-ui/icons/Assignment';
import SvgOrganization from '@material-ui/icons/SupervisorAccount';
import SvgApi from '@material-ui/icons/Code';
import SvgDefault from '@material-ui/icons/HelpOutline';

import { helper, http, api } from '../../common/common';
import InviteForm from './manage/InviteForm';
import InviteFormSuccess from './manage/InviteFormSuccess';
import MemberEntry from './manage/MemberEntry';
import InviteEntry from './manage/InviteEntry';

const styles = (theme) => {
    return {
        view: {
            position: 'absolute',
            overflow: 'auto',
            backgroundColor: 'rgba(255,255,255,0.97)',
            textAlign: 'center',
            top: 0,
            bottom: 0,
            left: 0,
            right: 0,
        },
        container: {
            width: '80%',
            minWidth: 640,
            minHeight: 480,
            height: '90%',
            marginTop: 24,
            marginBottom: 24,
            marginLeft: 'auto',
            marginRight: 'auto',
        },
        closeButton: {
            position: 'absolute',
            height: 48,
            top: 8,
            right: 8,
        },
        headingText: {
            width: '100%',
            color: theme.palette.text.primary,
        },
        subheadingText: {
            width: '100%',
            color: theme.palette.text.secondary,
        },
        previewBlock: {
            width: '100%',
            height: 'calc(100% - 48px)',
            overflow: 'auto',
        },
        section: {
            width: '95%',
            maxHeight: '75%',
            marginTop: 32,
            marginLeft: 'auto',
            marginRight: 'auto',
            marginBottom: 24,
            overflow: 'hidden',
        },
        sectionTitleBlock: {
            display: 'inline-block',
            width: '100%',
            height: 48,
            textAlign: 'left',
            backgroundColor: 'rgba(206, 142, 64, 0.17)',
        },
        sectionTitleText: {
            display: 'inline-block',
            marginLeft: 16,
            height: '100%',
            fontSize: 18,
            lineHeight: '48px',
            color: theme.palette.text.secondary,
            verticalAlign: 'middle',
            fontVariant: 'small-caps',
        },
        sectionTitleNumber: {
            display: 'inline-block',
            marginLeft: 16,
            height: '100%',
            fontSize: 15,
            lineHeight: '48px',
            color: theme.palette.text.secondary,
            verticalAlign: 'middle',
            fontVariant: 'small-caps',
        },
        sectionContentBlock: {
            height: 36,
            paddingLeft: 0,
            paddingRight: 0,
        },
        sectionLabelText: {
            float: 'left',
            height: 36,
            lineHeight: '36px',
            marginLeft: 16,
            color: theme.palette.text.secondary,
            verticalAlign: 'middle',
            fontVariant: 'small-caps',
            cursor: 'default',
            fontSize: 12,
            '&$clickable': {
                color: theme.palette.secondary.main,
                cursor: 'pointer',
                textTransform: 'uppercase',
                fontStyle: 'italic',
            },
        },
        clickable: {},
        iconHeader: {
            height: 48,
            float: 'right',
        },
        appIcon: {
            ...theme.iconStyle.size[24],
            ...theme.iconStyle.dark,
            paddingTop: 10,
            paddingBottom: 10,
            paddingLeft: 18,
            paddingRight: 18,
        },
        spinnerBackdrop: {
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.1)',
        },
        spinnerPlaceholder: {
            position: 'absolute',
            top: 'calc(50% - 40px)',
            left: 'calc(50% - 40px)',
            textAlign: 'center',
        },
        spinner: {
            color: theme.palette.text.disabled,
        },
        controlGroup: {
            width: '95%',
            height: 36,
            paddingTop: 16,
            paddingBottom: 16,
            marginLeft: 'auto',
            marginRight: 'auto',
            textAlign: 'right',
        },
        control: {
            float: 'right',
            marginRight: 0,
            marginLeft: 16,
        },
    };
}

const InviteState = Object.freeze({
    close: 0,
    request: 1,
    success: 2,
});

class Manage extends Component {
    constructor(props) {
        super(props);

        this.state = {
            timestamp: 0,
            showInviteFrom: InviteState.close,
        };

        this._cancelToken = http.CancelToken.source();
        this._pending = true;

        this._appList = []
        this._memberList = [];
        this._inviteList = [];
        this._accountlimit = [];
    }

    shouldComponentUpdate = (nextProps, nextState) => {
        return (this.props.open !== nextProps.open ||
            this.state.timestamp !== nextState.timestamp ||
            this.state.showInviteFrom !== nextState.showInviteFrom);
    }

    componentDidUpdate = (prevProps, prevState) => {
        if (this.props.open && prevProps.open !== this.props.open) {
            this._pending = true;

            this._requestReload();
        }
    }

    componentWillUnmount = () => {
        if (this._cancelToken) {
            this._cancelToken.cancel();
        }
    }

    render = () => {
        if (!this.props.open) {
            return null;
        }

        const { classes } = this.props;

        const memberViewHeight = ((this._memberList.length) ? (this._memberList.length * 36) : 0);
        const inviteViewHeight = ((this._inviteList.length) ? (this._inviteList.length * 36) : 0);
        const viewHeight = 36 + 48 + memberViewHeight + this._memberList.length +
            inviteViewHeight + this._inviteList.length;

        return (
            <div className={classes.view}>
                <div className={classes.container}>
                    <IconButton
                        className={classes.closeButton}
                        onClick={this._handleCancel}
                    >
                        <SvgClose />
                    </IconButton>
                    <Typography
                        className={classes.headingText}
                        align='center'
                        variant='h4'
                    >
                        API Token Magement
                    </Typography>
                    <Typography
                        className={classes.subheadingText}
                        align='center'
                        variant='subtitle1'
                    >
                        Manage your API Tokens
                    </Typography>
                    <Paper
                        className={classes.section}
                        style={{ height: viewHeight }}
                        elevation={1}
                    >
                        {this._renderHeader()}
                        <div className={classes.previewBlock}>
                            {this._memberList.map(this._renderMemberEntry)}
                            {this._inviteList.map(this._renderInviteEntry)}
                            <div className={classes.sectionContentBlock}>
                                <Typography
                                    className={classNames(classes.sectionLabelText, classes.clickable)}
                                    align='left'
                                    variant='subtitle1'
                                    onClick={this._handleInviteMemberRequest}
                                >
                                    Invite new member
                                </Typography>
                            </div>
                        </div>
                    </Paper>
                    {this._renderControls()}
                </div>
                {this._renderSpinner()}
                <InviteForm
                    open={this.state.showInviteFrom === InviteState.request}
                    onOk={this._handleInviteMemberOk}
                    onCancel={this._handleInviteMemberCancel}
                />
                <InviteFormSuccess
                    open={this.state.showInviteFrom === InviteState.success}
                    onOk={this._handleInviteMemberAcknowledge}
                />
            </div>
        );
    }

    _renderHeader = () => {
        const { classes } = this.props;

        return (
            <div className={classes.sectionTitleBlock}>
                <Typography
                    className={classes.sectionTitleText}
                    align='left'
                    variant='h6'
                >
                    API Token
                </Typography>
                <div className={classes.sectionTitleBlock}>
                    <Typography
                        className={classes.sectionTitleText}
                        align='left'
                        variant='h6'
                    >
                        Status
                    </Typography>
                </div>
                <div className={classes.sectionTitleBlock}>
                    <Typography
                        className={classes.sectionTitleText}
                        align='left'
                        variant='h6'
                    >
                        Created
                    </Typography>
                </div>
                <div className={classes.sectionTitleBlock}>
                    <Typography
                        className={classes.sectionTitleText}
                        align='left'
                        variant='h6'
                    >
                        Last Accessed
                    </Typography>
                </div>
                <div className={classes.sectionTitleBlock}>
                    <Typography
                        className={classes.sectionTitleText}
                        align='left'
                        variant='h6'
                    >
                        Manage
                    </Typography>
                </div>
            </div>
        );
    }


    _renderHeaderLabel = (data, arr, index) => {
        const { classes } = this.props;

        switch (data) {
            case 'viewer':
                return (
                    <Tooltip
                        id='tooltip-app'
                        title='Viewer Access'
                        enterDelay={500}
                    >
                        <SvgViewer className={classes.appIcon} />
                    </Tooltip>
                );
            case 'tasking':
                return (
                    <Tooltip
                        id='tooltip-app'
                        title='Tasking Access'
                        enterDelay={500}
                    >
                        <SvgTasking className={classes.appIcon} />
                    </Tooltip>
                );
            case 'report':
                return (
                    <Tooltip
                        id='tooltip-app'
                        title='Report Access'
                        enterDelay={500}
                    >
                        <SvgReport className={classes.appIcon} />
                    </Tooltip>
                );
            case 'api':
                return (
                    <Tooltip
                        id='tooltip-app'
                        title='API Access'
                        enterDelay={500}
                    >
                        <SvgApi className={classes.appIcon} />
                    </Tooltip>
                );
            case 'organization':
                return (
                    <Tooltip
                        id='tooltip-app'
                        title='Manage Organization'
                        enterDelay={500}
                    >
                        <SvgOrganization className={classes.appIcon} />
                    </Tooltip>
                );
            default:
                return (
                    <Tooltip
                        id='tooltip-app'
                        title={data}
                        enterDelay={500}
                    >
                        <SvgDefault className={classes.appIcon} />
                    </Tooltip>
                );
        }
    }

    _renderMemberEntry = (data, index, arr) => {
        return (
            <MemberEntry
                key={data.user_name}
                info={data}
                infoIndex={index}
                entryKeyList={this._appList}
                onChange={this._handleOnChange}
                onRemove={this._handleRemoveMember}
            />
        )
    }

    _renderInviteEntry = (data, index, arr) => {
        return (
            <InviteEntry
                key={data.user_name}
                info={data}
                infoIndex={index}
                onRemove={this._handleRemoveInvite}
            />
        )
    }

    _renderControls = () => {
        const { classes } = this.props;

        return (
            <div className={classes.controlGroup}>
                <Button
                    className={classes.control}
                    color='secondary'
                    autoFocus={true}
                    variant='contained'
                    onClick={this._handleSave}
                >
                    Save
                </Button>
                <Button
                    className={classes.control}
                    color='secondary'
                    onClick={this._handleCancel}
                >
                    Cancel
                </Button>
            </div>
        )
    }

    _renderSpinner = () => {
        if (!this._pending) {
            return null;
        }

        const { classes } = this.props;

        return (
            <div className={classes.spinnerBackdrop}>
                <div className={classes.spinnerPlaceholder}>
                    <CircularProgress
                        className={classes.spinner}
                        size={80}
                    />
                </div>
            </div>
        );
    }

    _handleInviteMemberRequest = (event) => {
        event.preventDefault();

        this.setState({
            showInviteFrom: InviteState.request,
        });
    }

    _handleInviteMemberOk = (user) => {
        this._pending = true;

        this.setState({
            showInviteFrom: InviteState.close,
            timestamp: this.state.timestamp - 1,
        });

        this._requestInviteMember(user);
    }

    _handleInviteMemberAcknowledge = () => {
        this.setState({
            showInviteFrom: InviteState.close,
        });
    }

    _handleInviteMemberCancel = () => {
        this.setState({
            showInviteFrom: InviteState.close,
        });
    }

    _handleSave = (event) => {
        event.preventDefault();

        for (const m of this._memberList) {
            m.access = [];

            Object.entries(m.accessMap).forEach(([index, value]) => {
                if (value) {
                    m.access.push(index);
                }
            });

            m.accessMap = {};
        }

        this._requestUpdateMember(this._memberList);
    }

    _handleCancel = (event) => {
        event.preventDefault();

        for (const m of this._memberList) {
            if (m.origAccessMap) {
                m.accessMap = m.origAccessMap;
                m.origAccessMap = null;
            }
        }

        if (this.props.onCancel) {
            this.props.onCancel();
        }
    }

    _handleOnChange = (index, name) => {
        const m = this._memberList[index];

        if (!m.origAccessMap) {
            m.origAccessMap = {};
            Object.assign(m.origAccessMap, m.accessMap);
        }

        m.accessMap[name] = (!m.accessMap[name]);

        for (const m of this._memberList) {
            m.access = [];

            Object.entries(m.accessMap).forEach(([index, value]) => {
                if (value) {
                    m.access.push(index);
                }
            });
        }

        this.setState({
            timestamp: Date.now(),
        });
    }

    _handleRemoveMember = (index) => {
        this._pending = true;

        this.setState({
            timestamp: this.state.timestamp - 1,
        });

        this._requestRemoveMember(this._memberList[index]);
    }

    _handleRemoveInvite = (index) => {
        this._pending = true;

        this.setState({
            timestamp: this.state.timestamp - 1,
        });

        this._requestRemoveInvite(this._inviteList[index]);
    }

    _processError = (error) => {
        if (this.props.onError) {
            this.props.onError(error);
        }
    }

    _sortMember = (left, right) => {
        if (left.role === 'owner' && right.role !== 'owner') {
            return -1;
        } else if (left.role !== 'owner' && right.role === 'owner') {
            return 1;
        } else {
            return left.user_name.localeCompare(right.user_name, 'en', { sensitivity: 'base' });
        }
    }

    _sortInvitation = (left, right) => {
        if (left.created < right.created) {
            return -1;
        } else if (left.created > right.created) {
            return 1;
        } else {
            return 0;
        }
    }

    _requestReload = async () => {
        try {
            await Promise.all([this._requestAppList(),
            this._requestMemberList(), this._requestInviteList(), this._requestAccountLimit()]);

            this._pending = false;

            this.setState({
                timestamp: Date.now(),
            });
        } catch (error) {
            this._pending = false;

            this.setState({
                timestamp: Date.now() - 10,
            });
        }
    }

    _requestAppList = async () => {
        try {
            const response = await http.get(api.privilege() + '/app', {
                params: {
                    'access_code': this.props.getAccessCode(),
                    'mode': 'global',
                },
                cancelToken: this._cancelToken.token,
            });

            if (response.data.access) {
                this._appList = response.data.access;
            } else {
                this._appList = [];
            }
        } catch (error) {
            if (http.isCancel(error)) {
                return;
            }

            this._pending = false;

            this._appList = [];

            this.setState({
                timestamp: Date.now(),
            });

            const e = helper.sanitizeError(error, 'Load Failed', 'Unable to load app list');
            this._processError(e);
        }
    }

    _requestMemberList = async () => {
        try {
            const response = await http.get(api.organization() + '/member', {
                params: {
                    'access_code': this.props.getAccessCode(),
                },
                cancelToken: this._cancelToken.token,
            });

            this._memberList = response.data.member_data;
            this._memberList.sort(this._sortMember);

            for (const m of this._memberList) {
                m.accessMap = {};

                for (const a of m.access) {
                    m.accessMap[a] = true;
                }
            }
        } catch (error) {
            if (http.isCancel(error)) {
                return;
            }

            this._pending = false;

            this.setState({
                timestamp: Date.now(),
            });

            const e = helper.sanitizeError(error, 'Load Failed', 'Unable to load member list');
            this._processError(e);
        }
    }

    _requestAccountLimit = async () => {
        try {
            const response = await http.get(api.organization() + '/accountlimit', {
                params: {
                    'access_code': this.props.getAccessCode(),
                },
                cancelToken: this._cancelToken.token,
            });
            this._accountlimit = response.data.account_limit;

        } catch (error) {
            if (http.isCancel(error)) {
                return;
            }
            this._pending = false;

            this.setState({
                timestamp: Date.now(),
            });

            const e = helper.sanitizeError(error, 'Load Failed', 'Unable to load account limit');
            this._processError(e);
        }
    }

    _requestInviteList = async () => {
        try {
            const response = await http.get(api.invite() + '/entry', {
                params: {
                    'access_code': this.props.getAccessCode(),
                },
                cancelToken: this._cancelToken.token,
            });

            this._inviteList = response.data.invite_data;
            this._inviteList.sort(this._sortInvitation);
        } catch (error) {
            if (http.isCancel(error)) {
                return;
            }

            this._pending = false;

            this.setState({
                timestamp: Date.now(),
            });

            const e = helper.sanitizeError(error, 'Load Failed', 'Unable to load invite list');
            this._processError(e);
        }
    }

    _requestUpdateMember = async (memberList) => {
        try {
            await http.post(api.organization() + '/member', {
                'access_code': this.props.getAccessCode(),
                'member_data': memberList,
            }, {
                    cancelToken: this._cancelToken.token,
                });

            if (this.props.onSave) {
                this.props.onSave();
            }
        } catch (error) {
            if (http.isCancel(error)) {
                return;
            }

            if (this.props.onSave) {
                this.props.onSave();
            }
        }
    }

    _requestRemoveMember = async (memberEntry) => {
        try {
            await http.delete(api.organization() + '/member', {
                params: {
                    'access_code': this.props.getAccessCode(),
                    'account_id': memberEntry.account_id,
                }
            }, {
                    cancelToken: this._cancelToken.token,
                });

            await this._requestMemberList();

            this._pending = false;

            this.setState({
                timestamp: Date.now(),
            });
        } catch (error) {
            if (http.isCancel(error)) {
                return;
            }

            this._pending = false;

            this.setState({
                timestamp: Date.now(),
            });

            const e = helper.sanitizeError(error, 'Remove Failed', 'Unable to remove member');
            this._processError(e);
        }
    }

    _requestRemoveInvite = async (inviteEntry) => {
        try {
            await http.delete(api.invite() + '/entry', {
                params: {
                    'access_code': this.props.getAccessCode(),
                    'invite_id': inviteEntry.id,
                }
            }, {
                    cancelToken: this._cancelToken.token,
                });

            await this._requestInviteList();

            this._pending = false;

            this.setState({
                timestamp: Date.now(),
            });
        } catch (error) {
            if (http.isCancel(error)) {
                return;
            }

            this._pending = false;

            this.setState({
                timestamp: Date.now(),
            });

            const e = helper.sanitizeError(error, 'Remove Failed', 'Unable to remove invitation');
            this._processError(e);
        }
    }

    _requestInviteMember = async (userName) => {
        try {
            await http.post(api.invite() + '/entry', {
                'access_code': this.props.getAccessCode(),
                'user_name': userName,
            }, {
                    cancelToken: this._cancelToken.token,
                });

            await this._requestInviteList();

            this._pending = false;

            this.setState({
                showInviteFrom: InviteState.success,
                timestamp: Date.now(),
            });
        } catch (error) {
            if (http.isCancel(error)) {
                return;
            }

            this._pending = false;

            this.setState({
                timestamp: Date.now(),
            });
            if (error.response.status === 403) {
                const e = helper.sanitizeError(error, 'Invite Failed', 'Account limit exceeded', 'warn_only');
                this._processError(e);
                console.log(error.response)
            } else {
                const e = helper.sanitizeError(error, 'Invite Failed', 'Unable to invite user', 'warn_only');
                this._processError(e);
                console.log(error.response)
            }

        }
    }
}

Manage.propTypes = {
    open: PropTypes.bool.isRequired,
    getAccessCode: PropTypes.func.isRequired,
    onSave: PropTypes.func,
    onCancel: PropTypes.func,
    onError: PropTypes.func,
}

export default withStyles(styles)(Manage);
